// src/pages/settings/xoe/XoeMemoryTab.tsx
import React, { useMemo, useState } from "react";
import { Alert, Button, Collapse, Input, List, Popconfirm, Space, Tag, Typography } from "antd";
import { CheckOutlined, CloseOutlined, PlusOutlined, UndoOutlined } from "@ant-design/icons";
import axios from "axios";
import { getSessionUser } from "../../../utils/auth/sessionUser";

import SectionCard from "../../../components/shared/SectionCard";
import InlineLoading from "../../../components/shared/InlineLoading";
import { useNotifications } from "../../../context/NotificationContext";
import {
	useXoeMemories,
	useCreateXoeMemory,
	useConfirmXoeMemory,
	useDismissXoeMemory,
	useWithdrawXoeMemory,
	useRestoreXoeMemory,
} from "../../../hooks/xoe/useXoeMemories";
import { XoeMemory, XoeMemoryScope, XOE_MEMORY_MAX_LENGTH } from "../../../types/xoe/XoeMemoryTypes";
import { extractValidationErrors } from "../../../utils/apiError";
import { formatInZone } from "../../../utils/time";
import type { UserUuid } from "../../../types/identity";

const { Text } = Typography;

// The D33 governance refusals, shown as warnings rather than errors — nothing is broken.
export const WITHDRAW_FORBIDDEN_MESSAGE =
	"Only the practitioner who confirmed this, or an administrator, can withdraw it";
export const RESTORE_FORBIDDEN_MESSAGE =
	"Only the practitioner who confirmed this, its proposer, or an administrator, can restore it";

const DATE_FORMAT = "D MMM YYYY";

const httpStatus = (error: unknown): number | null =>
	axios.isAxiosError(error) ? (error.response?.status ?? null) : null;

const detailOf = (error: unknown): string => extractValidationErrors(error).join(" ");

// Practitioner ids only reach the wire; names are not served, so a row reads "you" or "a practitioner".
const useCurrentUserId = (): UserUuid | undefined =>
	useMemo(() => {
		try {
			return (getSessionUser() as { id?: UserUuid }).id;
		} catch {
			return undefined;
		}
	}, []);

const who = (id: UserUuid | null | undefined, me: UserUuid | undefined): string =>
	id != null && id === me ? "you" : "a practitioner";

const isAdminRow = (row: XoeMemory): boolean => row.source === "admin";

interface ScopeSectionProps {
	scope: XoeMemoryScope;
	title: string;
	subtitle: string;
	placeholder: string;
	rows: XoeMemory[];
	me: UserUuid | undefined;
}

// One scope: proposals (Confirm/Dismiss), confirmed (Withdraw), an Add composer, and the 30-day withdrawn drawer (Restore).
const ScopeSection: React.FC<ScopeSectionProps> = ({ scope, title, subtitle, placeholder, rows, me }) => {
	const { showSuccess, showError, showWarning } = useNotifications();
	const [draft, setDraft] = useState("");
	const [showWithdrawn, setShowWithdrawn] = useState(false);

	const proposals = rows.filter((r) => r.status === "proposed" && !isAdminRow(r));
	const confirmed = rows.filter((r) => r.status === "confirmed" && !isAdminRow(r));
	const withdrawnQuery = useXoeMemories(scope, "withdrawn", showWithdrawn);
	const withdrawn = (withdrawnQuery.data ?? []).filter((r) => !isAdminRow(r));

	const create = useCreateXoeMemory();
	const confirm = useConfirmXoeMemory();
	const dismiss = useDismissXoeMemory();
	const withdraw = useWithdrawXoeMemory();
	const restore = useRestoreXoeMemory();

	const fail = (title: string) => (error: unknown) => showError(title, detailOf(error));

	const handleWithdraw = (id: string) =>
		withdraw.mutate(id, {
			onError: (error) =>
				httpStatus(error) === 403 ? showWarning(WITHDRAW_FORBIDDEN_MESSAGE) : fail("Could not withdraw")(error),
		});

	const handleRestore = (id: string) =>
		restore.mutate(id, {
			onError: (error) =>
				httpStatus(error) === 403 ? showWarning(RESTORE_FORBIDDEN_MESSAGE) : fail("Could not restore")(error),
		});

	const handleAdd = () => {
		const content = draft.trim();
		if (!content) return;
		create.mutate(
			{ scope, content },
			{
				onSuccess: () => {
					setDraft("");
					showSuccess("Xoe will remember that");
				},
				onError: fail("Could not add"),
			}
		);
	};

	const provenance = (row: XoeMemory): string => {
		const parts: string[] = [];
		if (scope === "clinic") parts.push(`proposed by ${who(row.practitioner_id, me)}`);
		if (row.origin_conversation_uuid) parts.push("from a conversation");
		return parts.join(" · ");
	};

	return (
		<SectionCard title={title} subtitle={subtitle}>
			<div style={{ display: "flex", flexDirection: "column", gap: 16 }} data-testid={`xoe-memory-section-${scope}`}>
				{proposals.length > 0 && (
					<List
						size="small"
						header={<Text className="mg-label">Waiting for your review</Text>}
						dataSource={proposals}
						renderItem={(row) => (
							<List.Item
								data-testid="xoe-memory-proposal"
								actions={[
									<Button
										key="confirm"
										size="small"
										type="primary"
										icon={<CheckOutlined />}
										loading={confirm.isPending && confirm.variables === row.id}
										onClick={() =>
											confirm.mutate(row.id, {
												onSuccess: () => showSuccess("Xoe will remember that"),
												onError: fail("Could not confirm"),
											})
										}
										data-testid="xoe-memory-confirm"
									>
										Confirm
									</Button>,
									<Button
										key="dismiss"
										size="small"
										icon={<CloseOutlined />}
										loading={dismiss.isPending && dismiss.variables === row.id}
										onClick={() => dismiss.mutate(row.id, { onError: fail("Could not dismiss") })}
										data-testid="xoe-memory-dismiss"
									>
										Dismiss
									</Button>,
								]}
							>
								<List.Item.Meta
									title={
										<Space size={8}>
											<Tag color="processing" style={{ margin: 0 }}>Xoe suggested</Tag>
											<Text style={{ fontSize: 12, fontWeight: 400 }}>{row.content}</Text>
										</Space>
									}
									description={
										<Text type="secondary" style={{ fontSize: 11 }}>
											{[provenance(row), formatInZone(row.created_at, DATE_FORMAT)].filter(Boolean).join(" · ")}
										</Text>
									}
								/>
							</List.Item>
						)}
					/>
				)}

				<List
					size="small"
					dataSource={confirmed}
					locale={{
						emptyText: (
							<Text type="secondary" style={{ fontSize: 12 }}>
								Nothing confirmed yet — Xoe will suggest things to remember as you work.
							</Text>
						),
					}}
					renderItem={(row) => (
						<List.Item
							data-testid="xoe-memory-confirmed"
							actions={[
								<Popconfirm
									key="withdraw"
									title="Withdraw this memory?"
									description="Xoe stops using it immediately. It can be restored for 30 days."
									okText="Withdraw it"
									cancelText="Cancel"
									okButtonProps={{ danger: true }}
									onConfirm={() => handleWithdraw(row.id)}
								>
									<Button size="small" type="text" danger data-testid="xoe-memory-withdraw">
										Withdraw
									</Button>
								</Popconfirm>,
							]}
						>
							<List.Item.Meta
								title={<Text style={{ fontSize: 12, fontWeight: 400 }}>{row.content}</Text>}
								description={
									<Text type="secondary" style={{ fontSize: 11 }}>
										{[
											`confirmed by ${who(row.confirmed_by, me)}`,
											formatInZone(row.confirmed_at ?? row.created_at, DATE_FORMAT),
											scope === "clinic" && row.source === "model" ? `proposed by ${who(row.practitioner_id, me)}` : "",
											row.has_embedding ? "" : "not yet searchable by relevance",
										]
											.filter(Boolean)
											.join(" · ")}
									</Text>
								}
							/>
						</List.Item>
					)}
				/>

				<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
					<Input.TextArea
						value={draft}
						onChange={(e) => setDraft(e.target.value)}
						placeholder={placeholder}
						maxLength={XOE_MEMORY_MAX_LENGTH}
						showCount
						autoSize={{ minRows: 2, maxRows: 4 }}
						data-testid="xoe-memory-add-input"
					/>
					<div style={{ display: "flex", justifyContent: "flex-end" }}>
						<Button
							type="primary"
							size="small"
							icon={<PlusOutlined />}
							disabled={!draft.trim()}
							loading={create.isPending}
							onClick={handleAdd}
							data-testid="xoe-memory-add-submit"
						>
							Add
						</Button>
					</div>
				</div>

				<Collapse
					ghost
					size="small"
					onChange={(keys) => setShowWithdrawn((Array.isArray(keys) ? keys : [keys]).length > 0)}
					items={[
						{
							key: "withdrawn",
							label: <Text type="secondary" style={{ fontSize: 12 }}>Recently withdrawn</Text>,
							children: withdrawnQuery.isLoading ? (
								<InlineLoading minHeight={60} message="Loading…" />
							) : (
								<List
									size="small"
									dataSource={withdrawn}
									locale={{
										emptyText: (
											<Text type="secondary" style={{ fontSize: 12 }}>Nothing withdrawn in the last 30 days.</Text>
										),
									}}
									renderItem={(row) => (
										<List.Item
											data-testid="xoe-memory-withdrawn"
											actions={[
												<Button
													key="restore"
													size="small"
													icon={<UndoOutlined />}
													loading={restore.isPending && restore.variables === row.id}
													onClick={() => handleRestore(row.id)}
													data-testid="xoe-memory-restore"
												>
													Restore
												</Button>,
											]}
										>
											<List.Item.Meta
												title={<Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>{row.content}</Text>}
												description={
													<Text type="secondary" style={{ fontSize: 11 }}>
														{`withdrawn ${formatInZone(row.withdrawn_at, DATE_FORMAT)}`}
													</Text>
												}
											/>
										</List.Item>
									)}
								/>
							),
						},
					]}
				/>
			</div>
		</SectionCard>
	);
};

// Settings › Xoe Memory (14ygtkj83tv S10) — the one confirm surface for what Xoe remembers across conversations.
const XoeMemoryTab: React.FC = () => {
	const me = useCurrentUserId();
	const practitioner = useXoeMemories("practitioner");
	const clinic = useXoeMemories("clinic");

	const retry = () => {
		practitioner.refetch();
		clinic.refetch();
	};

	if ((practitioner.isLoading || clinic.isLoading) && !practitioner.isError && !clinic.isError) {
		return <InlineLoading minHeight={300} message="Loading Xoe memory…" />;
	}

	if (practitioner.isError || clinic.isError) {
		return (
			<SectionCard title="Xoe memory">
				<Alert
					type="error"
					showIcon
					message="Xoe memory is unavailable"
					description={
						detailOf(practitioner.error ?? clinic.error) ||
						"The Xoe assistant service could not be reached, so memory cannot be shown or changed right now."
					}
					action={<Button onClick={retry}>Retry</Button>}
				/>
			</SectionCard>
		);
	}

	const practitionerRows = practitioner.data ?? [];
	const clinicRows = clinic.data ?? [];
	// Admin rows may sit in either scope; they are governed by MyGene and read-only here.
	const adminRows = [...practitionerRows, ...clinicRows].filter(isAdminRow);

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 16 }} data-testid="xoe-memory-tab">
			<ScopeSection
				scope="practitioner"
				title="My preferences"
				subtitle="How Xoe works with you. Only you can see these; Xoe suggests them as you talk and you confirm."
				placeholder="Something Xoe should remember about how you work…"
				rows={practitionerRows}
				me={me}
			/>
			<ScopeSection
				scope="clinic"
				title="Clinic knowledge"
				subtitle="Shared with every practitioner at this clinic. Anyone can confirm a suggestion; only the practitioner who confirmed it, or an administrator, can withdraw it."
				placeholder="Something every practitioner's Xoe should know about this clinic…"
				rows={clinicRows}
				me={me}
			/>
			<SectionCard title="From MyGene" subtitle="Provided by MyGene for every clinic. Read-only.">
				<div data-testid="xoe-memory-section-admin">
					<List
						size="small"
						dataSource={adminRows}
						locale={{
							emptyText: <Text type="secondary" style={{ fontSize: 12 }}>Nothing from MyGene yet.</Text>,
						}}
						renderItem={(row) => (
							<List.Item data-testid="xoe-memory-admin">
								<List.Item.Meta
									title={
										<Text style={{ fontSize: 12, fontWeight: row.title ? 600 : 400 }}>
											{row.title || row.content}
										</Text>
									}
									description={
										<>
											{row.title && (
												<Text style={{ display: "block", fontSize: 12 }}>{row.content}</Text>
											)}
											<Text type="secondary" style={{ fontSize: 11 }}>
												{row.expires_at
													? `Expires ${formatInZone(row.expires_at, DATE_FORMAT)}`
													: "Does not expire"}
											</Text>
										</>
									}
								/>
							</List.Item>
						)}
					/>
				</div>
			</SectionCard>
		</div>
	);
};

export default XoeMemoryTab;
